import type { Field, ComponentParams, ComponentRendering } from "@sitecore-jss/sitecore-jss-nextjs";
import {
    Box,
    Button,
    Link,
    Heading,
    Text,
    Flex,
    VisuallyHidden,
    List,
    ListItem,
    Stack,
} from "@chakra-ui/react";
import type { GenericHeaderProps } from "commons/ui/GenericHeader";
import GenericHeader from "commons/ui/GenericHeader";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import NextLink from "next/link";
import { useI18n } from "next-localization";
import { useRouter } from "next/router";
import BundleIcon from "commons/ui/BundleIcon";
import type { FormattedJobDataProps, JobProps } from "src/pages/api/career/types";
import { SkeletonContainerList } from "commons/ui/SkeletonContainer";
import { FilterLoadmoreHandler } from "commons/ui/FilterUI";

interface Fields extends GenericHeaderProps {
    JobCategoryLabel: Field<string>;
    LocationLabel: Field<string>;
    SummaryText: Field<string>;
    HideFilters: Field<boolean>;
    PreSelectedLocations: Field<string>;
    PreselectedJobCategories: Field<string>;
    CampaignName: Field<string>;
}

export type CareerProps = {
    rendering: ComponentRendering & { params: ComponentParams };
    params: { [key: string]: string };
    fields: Fields;
};

interface JobDataProps extends FormattedJobDataProps {
    firstNewResultIndex: number;
    hasNext: boolean;
}

const WORKDAY_ID_REGEX = /^[a-f0-9]{32}$/i;
const NUMBER_OF_RESULTS_PER_PAGE = 12;

// Helper to parse comma-separated or single value strings into arrays
const parseFieldValue = (value?: string): string[] => {
    if (!value) return [];
    return value.includes(",")
        ? value
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean)
        : [value];
};

// Helper to check if query is empty (only has 'path' parameter)
const isQueryEmpty = (query: Record<string, unknown>): boolean => {
    return Object.keys(query).filter((key) => key !== "path").length === 0;
};

const Career = (props: CareerProps) => {
    const campaignName = props.fields?.CampaignName.value;
    const [careerResults, setCareerResults] = useState<JobDataProps>({
        results: [],
        totalResults: 0,
        categories: [],
        countries: [],
        firstNewResultIndex: 0,
        hasNext: false,
    });
    const { push, query, isReady } = useRouter();
    const [queryString, setQueryString] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);
    const [openCategory, setOpenCategory] = useState<string>("");
    const [preFiltersApplied, setPreFiltersApplied] = useState<boolean>(false);

    const { t } = useI18n();
    const firstNewResultRef = useRef<HTMLAnchorElement>(null);

    // Parse pre-selected values from Sitecore fields
    const preSelectedLocations = useMemo(
        () => parseFieldValue(props.fields?.PreSelectedLocations?.value),
        [props.fields?.PreSelectedLocations?.value]
    );
    const preSelectedCategories = useMemo(
        () => parseFieldValue(props.fields?.PreselectedJobCategories?.value),
        [props.fields?.PreselectedJobCategories?.value]
    );

    const hideFilters = props.fields?.HideFilters?.value ?? false;
    const showClearAll = !isQueryEmpty(query);

    // Convert names or IDs to actual Workday IDs
    const convertToIds = useCallback(
        (
            values: string[],
            dataList: Array<{ id?: string; name: string; ids?: string[] }>,
            type: "location" | "category"
        ): string[] => {
            const ids: string[] = [];

            values.forEach((value) => {
                // If it's already a Workday ID, use it directly
                if (WORKDAY_ID_REGEX.test(value)) {
                    ids.push(value);
                    return;
                }

                // It's a name, find the matching item and get its ID(s)
                const match = dataList.find(
                    (item) => item.name.toLowerCase() === value.toLowerCase()
                );

                if (match) {
                    // For locations, use the ids array; for categories, use the id field
                    if (type === "location" && match.ids) {
                        ids.push(...match.ids);
                    } else if (type === "category" && match.id) {
                        ids.push(match.id);
                    }
                } else {
                    // eslint-disable-next-line no-console
                    console.warn(
                        `${type === "location" ? "Country" : "Category"} name "${value}" not found in API data`
                    );
                }
            });

            return ids;
        },
        []
    );

    const fetchResults = useCallback(async () => {
        setLoading(true);

        try {
            const queryDidChange = JSON.stringify(query) !== queryString;
            const requestBody = {
                category: query.category ?? "",
                country: query.country ?? "",
                offset: queryDidChange ? "0" : careerResults.results.length.toString(),
            };

            const response = await fetch(`/api/career`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error("Failed to fetch data");
            }

            const responseData = (await response.json()) as FormattedJobDataProps;

            setCareerResults((prevResults) => ({
                results: queryDidChange
                    ? responseData.results
                    : [...prevResults.results, ...responseData.results],
                categories: responseData.categories,
                countries: responseData.countries,
                totalResults: responseData.totalResults,
                firstNewResultIndex: queryDidChange ? 0 : prevResults.results.length,
                hasNext: responseData.results.length === NUMBER_OF_RESULTS_PER_PAGE,
            }));
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Error fetching career data:", error);
        } finally {
            setLoading(false);
        }
    }, [query, queryString, careerResults.results.length]);

    const onToggleTag = useCallback((name: string) => {
        setOpenCategory((prev) => (prev === name ? "" : name));
    }, []);

    const onSelectTag = useCallback(
        (categoryName: string, tagId: string | string[]) => {
            const updatedQuery = { ...query };
            let existingTags = (updatedQuery[encodeURIComponent(categoryName)] as string[]) || [];

            if (typeof existingTags === "string") {
                existingTags = [existingTags];
            }

            const idsToProcess = Array.isArray(tagId) ? tagId : [tagId];

            idsToProcess.forEach((id) => {
                const tagIndex = existingTags.indexOf(id);
                if (tagIndex !== -1) {
                    existingTags.splice(tagIndex, 1);
                } else {
                    existingTags.push(id);
                }
            });

            updatedQuery[encodeURIComponent(categoryName)] = existingTags;
            push({ query: updatedQuery }, undefined, { shallow: true });
        },
        [query, push]
    );

    const cleanAllFilters = useCallback(() => {
        setOpenCategory("");
        push({ query: { path: query["path"] } }, undefined, { shallow: true });
    }, [query, push]);

    // Apply pre-selected filters after data loads (only if query is empty)
    useEffect(() => {
        const hasData = careerResults.countries.length > 0 && careerResults.categories.length > 0;
        const hasPreselected = preSelectedLocations.length > 0 || preSelectedCategories.length > 0;
        const shouldApply =
            isReady && !preFiltersApplied && hasData && hasPreselected && isQueryEmpty(query);

        if (!shouldApply) return;

        const updatedQuery = { ...query };
        let hasChanges = false;

        // Convert and apply location filters
        if (preSelectedLocations.length > 0) {
            const locationIds = convertToIds(
                preSelectedLocations,
                careerResults.countries,
                "location"
            );
            if (locationIds.length > 0) {
                updatedQuery["country"] = locationIds;
                hasChanges = true;
            }
        }

        // Convert and apply category filters
        if (preSelectedCategories.length > 0) {
            const categoryIds = convertToIds(
                preSelectedCategories,
                careerResults.categories,
                "category"
            );
            if (categoryIds.length > 0) {
                updatedQuery["category"] = categoryIds;
                hasChanges = true;
            }
        }

        if (hasChanges) {
            push({ query: updatedQuery }, undefined, { shallow: true });
            setPreFiltersApplied(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReady, careerResults.countries, careerResults.categories, preFiltersApplied]);

    // Fetch results when query changes
    useEffect(() => {
        if (isReady) {
            fetchResults();
            setQueryString(JSON.stringify(query));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, isReady]);

    // Scroll to first new result after load more
    useEffect(() => {
        if (
            isReady &&
            firstNewResultRef?.current &&
            careerResults.hasNext &&
            careerResults.results.length > NUMBER_OF_RESULTS_PER_PAGE
        ) {
            firstNewResultRef.current.focus();
            firstNewResultRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "center",
            });
        }
    }, [careerResults, isReady]);

    const getLoadMoreLabel = () => {
        if (loading) return t("loadMoreLoadingA11yText");
        if (careerResults.hasNext) return t("fetchMoreResultsA11yText");
        return "";
    };
    const getJobUrlWithSource = (url: string, campaignName: string) => {
        if (!campaignName) return url;

        const separator = url.includes("?") ? "&" : "?";
        return `${url}${separator}source=${campaignName}`;
    };

    if (!props) return null;

    const showing = careerResults.results.length;
    const totalResults = careerResults.totalResults;
    const borderBottom = "1px solid lightGray";

    return (
        <GenericHeader
            Id={props.rendering?.uid ?? props.rendering?.componentName}
            AnchorId={props.fields?.AnchorId}
            HeaderHeadline={props.fields?.HeaderHeadline}
            HeaderSubHeadline={props.fields?.HeaderSubHeadline}
            HeaderCTA={props.fields?.HeaderCTA}
            headlineSize={props.params?.HeaderHeadlineSize}
            spacingSize={props.params?.Padding}
        >
            <>
                <Flex
                    gap={16}
                    pb={"sp24"}
                    mb={"sp24"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    position={"relative"}
                    borderBottom={borderBottom}
                    flexWrap={"wrap"}
                >
                    <Text width={{ base: "full", md: "auto" }}>
                        {props.fields?.SummaryText.value
                            .replace("{{totalResults}}", totalResults.toString())
                            .replace("{{showing}}", showing.toString())}
                    </Text>
                    {!hideFilters && (
                        <Flex
                            gap={16}
                            alignItems={"center"}
                            flexWrap={"wrap"}
                            width={{ base: "full", md: "auto" }}
                        >
                            <Flex
                                width={{ base: "100%", md: "auto" }}
                                flexDir={{ base: "row-reverse", md: "inherit" }}
                                justifyContent={{ base: "space-between", md: "inherit" }}
                            >
                                {showClearAll && (
                                    <Link
                                        as="button"
                                        variant={"clearAllLink"}
                                        onClick={cleanAllFilters}
                                        mr="sp24"
                                    >
                                        {t("clearAllButtonText")}
                                    </Link>
                                )}
                                <Text>{t("filterLabelText")}</Text>
                            </Flex>

                            <Button
                                variant={"outline"}
                                size={"sm"}
                                {...(openCategory === "country" && {
                                    sx: { span: { transform: "rotate(180deg)" } },
                                })}
                                onClick={() => onToggleTag("country")}
                                onKeyUp={(e) => {
                                    if (
                                        e.key === "Enter" ||
                                        (e.key === " " && openCategory === "country")
                                    ) {
                                        document
                                            .getElementById(`tagcategorytags-country`)
                                            ?.querySelector("button")
                                            ?.focus();
                                    }
                                }}
                                rightIcon={<BundleIcon name={"ExpandMore"} />}
                                id={`filterCategory-country`}
                                aria-controls={`filterCategory-country`}
                                aria-expanded={openCategory === "country"}
                                isLoading={loading}
                            >
                                {props.fields?.LocationLabel.value}{" "}
                                {Boolean(careerResults.countries.length) && (
                                    <>
                                        ({careerResults.countries.length})
                                        <VisuallyHidden>{t("selectedTagsA11yText")}</VisuallyHidden>
                                    </>
                                )}
                            </Button>

                            <Button
                                variant={"outline"}
                                size={"sm"}
                                {...(openCategory === "category" && {
                                    sx: { span: { transform: "rotate(180deg)" } },
                                })}
                                onClick={() => onToggleTag("category")}
                                onKeyUp={(e) => {
                                    if (
                                        e.key === "Enter" ||
                                        (e.key === " " && openCategory === "category")
                                    ) {
                                        document
                                            .getElementById(`tagcategorytags-category`)
                                            ?.querySelector("button")
                                            ?.focus();
                                    }
                                }}
                                rightIcon={<BundleIcon name={"ExpandMore"} />}
                                id={`filterCategory-category`}
                                aria-controls={`filterCategory-category`}
                                aria-expanded={openCategory === "category"}
                                isLoading={loading}
                            >
                                {props.fields?.JobCategoryLabel.value}{" "}
                                {Boolean(careerResults.categories.length) && (
                                    <>
                                        ({careerResults.categories.length})
                                        <VisuallyHidden>{t("selectedTagsA11yText")}</VisuallyHidden>
                                    </>
                                )}
                            </Button>
                        </Flex>
                    )}
                </Flex>

                {!hideFilters && (
                    <>
                        <Flex
                            id={`tagcategorytags-country`}
                            aria-labelledby={`tagcategory-country`}
                            hidden={openCategory !== "country"}
                            borderBottom={borderBottom}
                            pb={"sp24"}
                            gap={"sp16"}
                            flexWrap={"wrap"}
                            marginBottom={"sp40"}
                        >
                            {careerResults.countries?.map((country) => {
                                const selectedCountries = query["country"] || [];
                                const selected = country.ids.some((id) =>
                                    selectedCountries.includes(id)
                                );
                                return (
                                    <Button
                                        key={`${country.name}_option`}
                                        variant={selected ? "primary" : "outline"}
                                        size={"xs"}
                                        onClick={() => onSelectTag("country", country.ids)}
                                        aria-pressed={selected}
                                        {...(selected && {
                                            rightIcon: <BundleIcon name={"Check"} />,
                                        })}
                                    >
                                        {country.name}
                                    </Button>
                                );
                            })}
                        </Flex>

                        <Flex
                            id={`tagcategorytags-category`}
                            aria-labelledby={`tagcategory-category`}
                            hidden={openCategory !== "category"}
                            borderBottom={borderBottom}
                            pb={"sp24"}
                            gap={"sp16"}
                            flexWrap={"wrap"}
                            marginBottom={"sp40"}
                        >
                            {careerResults.categories?.map((category) => {
                                const selected = query["category"]?.includes(category.id);
                                return (
                                    <Button
                                        key={`${category.id}_option`}
                                        variant={selected ? "primary" : "outline"}
                                        size={"xs"}
                                        onClick={() => onSelectTag("category", category.id)}
                                        aria-pressed={selected}
                                        {...(selected && {
                                            rightIcon: <BundleIcon name={"Check"} />,
                                        })}
                                    >
                                        {category.name}
                                    </Button>
                                );
                            })}
                        </Flex>
                    </>
                )}
            </>

            <VisuallyHidden as="output" aria-live="polite">
                {getLoadMoreLabel()}
            </VisuallyHidden>

            {!loading && careerResults.results.length === 0 && query.q && (
                <Box mt={{ base: "sp40", lg: "sp88" }}>
                    <Text as="p" size="bodyXl">
                        {t("noSearchResults")}
                    </Text>
                </Box>
            )}

            {careerResults.results.length > 0 && (
                <List
                    display={"flex"}
                    mt={{ base: "sp40", lg: "sp88" }}
                    flexDir={"column"}
                    gap={"sp40"}
                >
                    {careerResults.results.map((result: JobProps, index: number) => {
                        const isFirstNewResult = index === careerResults.firstNewResultIndex;
                        return (
                            <ListItem key={result.url}>
                                <Link
                                    as={NextLink}
                                    ref={isFirstNewResult ? firstNewResultRef : null}
                                    href={getJobUrlWithSource(result.url, campaignName)}
                                    variant={"searchLink"}
                                    target="_blank"
                                >
                                    <Stack>
                                        <Heading
                                            as="h3"
                                            size="sm"
                                            textDecoration={"underline"}
                                            lineHeight={"base"}
                                            mb={{ base: "sp8", lg: "sp8" }}
                                        >
                                            {result.title}
                                        </Heading>
                                        <Text
                                            size="bodySmall"
                                            sx={{ ".max": { fontWeight: "bold" } }}
                                            display={"flex"}
                                            alignItems={"center"}
                                        >
                                            <BundleIcon name={"Label"} mr={"sp8"} />
                                            {result.categories
                                                ?.map((category) => category.name)
                                                .join(", ")}
                                        </Text>
                                        <Text
                                            size="bodySmall"
                                            display={"flex"}
                                            alignItems={"center"}
                                        >
                                            <BundleIcon name={"Location"} mr={"sp8"} />
                                            {result.countries
                                                .map((country) => country.name)
                                                .join(", ")}
                                        </Text>
                                    </Stack>
                                </Link>
                            </ListItem>
                        );
                    })}
                </List>
            )}

            {loading && (
                <SkeletonContainerList numberOfResultsPerPage={NUMBER_OF_RESULTS_PER_PAGE} />
            )}

            <FilterLoadmoreHandler
                loading={loading}
                hasNext={careerResults.hasNext}
                fetchSearchResults={fetchResults}
                ariaControls={props.rendering.uid}
            />
        </GenericHeader>
    );
};

export default Career;
